/**
 * Comprehensive tooltips for Financial Management and Financial Planning
 */

export const financialManagementTooltips = {
  // Loan Fields
  loanType: "Type of financing. Example: 'Term Loan' for traditional business loan, 'Line of Credit' for revolving credit",
  loanName: "Descriptive name for this loan. Example: 'ABC Bank Equipment Loan' or 'Working Capital Line'",
  lender: "Financial institution providing the loan. Example: 'Commercial Bank of Ethiopia', 'ABC Finance Corp'",
  principal: "Total amount borrowed. Example: $200,000 principal amount",
  interestType: "How interest rate is determined. Fixed = stays same, Variable = changes with market, Floating = tied to reference rate",
  fixedRate: "Annual interest percentage that stays constant. Example: 8.5% fixed for entire loan term",
  baseRate: "Starting reference rate for variable loans. Example: Prime Rate (currently 7%) or LIBOR",
  margin: "Additional percentage added to base rate. Example: Prime + 2% = 7% + 2% = 9% total",
  referenceRate: "Market rate used as base. Example: 'LIBOR', 'Prime Rate', 'Treasury Rate'",
  adjustmentFrequency: "How often variable rate adjusts. Example: Quarterly = rate recalculates every 3 months",
  rateCap: "Maximum interest rate limit. Example: 12% cap protects you if rates spike",
  rateFloor: "Minimum interest rate. Example: 5% floor protects lender if rates drop",
  term: "Loan duration in months. Example: 60 months = 5 years",
  paymentFrequency: "How often you make payments. Example: Monthly = 12 payments per year",
  startDate: "When loan begins and first payment is due. Example: January 1, 2025",
  originationFees: "One-time fees to set up loan. Example: $2,000 origination fee (1% of $200,000)",
  linkedInitiative: "Which strategic initiative this loan funds. Example: Link to 'Expand Production' initiative",
  owner: "Person managing this loan. Example: 'Sarah Johnson - CFO'",
  
  // Equity Fields
  authorizedShares: "Maximum number of shares company can issue as authorized in charter. Example: 10,000,000 authorized shares (room for growth)",
  issuedShares: "Actual number of shares given to shareholders. Example: 1,000,000 issued out of 10M authorized",
  parValue: "Nominal accounting value per share (usually very low). Example: $0.01 par value per share",
  additionalPaidInCapital: "Money received above par value when selling shares. Example: Sold shares at $10, par value $0.01 = $9.99 additional paid-in capital per share",
  retainedEarnings: "Cumulative profits kept in company (not paid as dividends). Example: $500,000 retained earnings from 3 years of profits",
  treasuryStock: "Company's own shares bought back from market. Example: $100,000 of treasury stock (reduces equity)",
  outstandingShares: "Total number of shares issued. Example: 1,000,000 shares outstanding",
  sharePrice: "Current price per share. Example: $10 per share",
  totalValue: "Total company valuation. AUTO-CALCULATED: Shares × Price. Example: 1M shares × $10 = $10M",
  founderShares: "Shares owned by founders. Example: 600,000 shares (60% ownership)",
  investorShares: "Shares owned by outside investors. Example: 300,000 shares (30%)",
  employeeShares: "Shares for employee stock options. Example: 100,000 shares (10%)",
  costOfEquity: "Expected return rate for equity investors. Example: 12% annual return expectation",
  
  // DSCR
  dscr: "Debt Service Coverage Ratio - measures ability to pay debt. Ratio > 1.25 is healthy, < 1.0 is risky. Example: DSCR of 1.5 means you earn $1.50 for every $1 of debt payment",
  
  // Amortization
  amortization: "Payment schedule showing how loan is repaid over time. Each payment splits between principal (reducing loan) and interest (cost of borrowing)"
};

export const financialPlanningTooltips = {
  // Planned Financing
  financingType: "Type of capital to raise. Example: 'Series A' for first major VC round, 'Bank Loan' for debt financing",
  targetAmount: "How much capital you plan to raise. Example: $2,000,000 Series A round",
  targetDate: "When you need the funds. Example: Q2 2026 to fund expansion",
  linkedInitiative: "Which strategic initiative this funding supports. Example: Link to 'Market Expansion' initiative",
  expectedInterestRate: "Anticipated interest rate for debt. Example: Expect 8.5% based on current market",
  expectedValuation: "Company valuation for equity round. Example: $10M pre-money valuation",
  expectedDilution: "Ownership percentage given up. Example: 20% dilution means founders go from 100% to 80%",
  
  // Milestones
  milestoneName: "Key achievement to track. Example: 'Reach 10,000 Users', 'Launch New Product'",
  milestoneDate: "Target completion date. Example: June 30, 2026",
  milestoneOwner: "Person responsible for achieving this. Example: 'Michael Chen - VP Product'",
  milestoneLinkedInitiative: "Strategic initiative this milestone belongs to. Example: Link to 'Product Launch' initiative",
  milestoneMetric: "How success is measured. Example: '10,000 active users' or '$1M ARR'",
  
  // Variance
  variance: "Difference between planned and actual. Example: +15% means performing 15% better than plan",
  plannedAmount: "What you forecasted. Example: Planned $100,000 revenue",
  actualAmount: "What actually happened. Example: Actual $115,000 revenue (15% variance)"
};

export const getFinancialTooltip = (section, field) => {
  if (section === 'management') {
    return financialManagementTooltips[field] || '';
  }
  if (section === 'planning') {
    return financialPlanningTooltips[field] || '';
  }
  return '';
};
